import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { KeyRound } from 'lucide-react'
import { SUPPORTED_LANGUAGES } from '@/i18n'
import { ActivityLog } from '@/components/ActivityLog'
import { PresetManageCard } from '@/components/PresetManageCard'
import { SubscriptionCard } from '@/components/SubscriptionCard'
import { ReminderSettingsCard } from '@/components/ReminderSettingsCard'
import { EmailTestCard } from '@/components/EmailTestCard'

function validatePassword(pw: string, t: (key: string) => string): string | null {
  if (!pw) return t('auth.errors.passwordRequired')
  if (pw.length < 8) return t('auth.errors.passwordMinLength')
  if (!/[a-zA-Z]/.test(pw)) return t('auth.errors.passwordLetters')
  if (!/[0-9]/.test(pw)) return t('auth.errors.passwordNumbers')
  return null
}

export function SettingsPage() {
  const { t, i18n } = useTranslation()

  // 切换语言：本地立即生效（i18n + localStorage），并 best-effort 同步到服务端用户偏好（OPT-005）
  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code)
    fetch('/api/auth/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ language: code }),
    }).catch(() => {})
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">{t('settings.title')}</h1>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.language')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {SUPPORTED_LANGUAGES.map((code) => (
              <Button
                key={code}
                variant={i18n.language === code ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleLanguageChange(code)}
              >
                {t(`languages.${code}`)}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <SubscriptionCard />

      <ReminderSettingsCard />

      <EmailTestCard />

      <PresetManageCard kind="note" />
      <PresetManageCard kind="location" />

      <ActivityLog />

      <ChangePasswordCard />

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.about')}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-1">
          <p>{t('settings.version', { appName: t('app.name') })}</p>
          <p>{t('settings.description')}</p>
          <p className="text-xs text-slate-400">{t('settings.aboutFooter')}</p>
        </CardContent>
      </Card>
    </div>
  )
}

function ChangePasswordCard() {
  const { t } = useTranslation()
  // OPT-017：改密从内嵌表单改为弹窗模式——页面仅留入口按钮，表单移入 Dialog
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)

  /** 关闭弹窗时重置表单 state（错误与输入不残留到下次打开） */
  const handleOpenChange = (o: boolean) => {
    setOpen(o)
    if (!o) {
      setCurrent(''); setNext(''); setConfirm(''); setError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    const nextErr = validatePassword(next, t)
    if (nextErr) { setError(nextErr); return }
    if (next !== confirm) { setError(t('settings.passwordsMismatch')); return }
    setLoading(true)
    try {
      const res = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      })
      if (!res.ok) {
        // 失败：错误显示在弹窗内，弹窗不关闭
        const body = await res.json().catch(() => ({}))
        const err = body?.error
        let message = err?.message || t('common.failed')
        if (err?.code === 'INVALID_CREDENTIALS') message = t('settings.currentPasswordIncorrect')
        if (err?.details?.currentPassword) message = t('settings.currentPasswordIncorrect')
        if (err?.details?.newPassword) message = err.details.newPassword.join(', ')
        if (err?.details?.password) message = err.details.password.join(', ')
        setError(message)
      } else {
        // 成功：页面级提示并关闭弹窗（关闭即重置表单）
        setSuccess(t('settings.passwordChanged'))
        handleOpenChange(false)
      }
    } catch {
      setError(t('common.networkError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> {t('settings.changePassword.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Button
          data-testid="open-change-password"
          variant="outline"
          onClick={() => { setSuccess(''); setOpen(true) }}
        >
          <KeyRound className="h-4 w-4 mr-2" /> {t('settings.changePassword.update')}
        </Button>
        {success && <p className="text-sm text-green-600 mt-2">{success}</p>}
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('settings.changePassword.title')}</DialogTitle>
            </DialogHeader>
            <form data-testid="change-password-form" onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="current">{t('settings.changePassword.current')}</Label>
                <Input id="current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="next">{t('settings.changePassword.new')}</Label>
                <Input id="next" type="password" value={next} onChange={(e) => setNext(e.target.value)} required />
                <p className="text-xs text-slate-400 mt-1">{t('auth.passwordHint')}</p>
              </div>
              <div>
                <Label htmlFor="confirm">{t('settings.changePassword.confirm')}</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>{t('common.cancel')}</Button>
                <Button type="submit" disabled={loading}>{loading ? t('common.saving') : t('settings.changePassword.update')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
